// src/hooks/useAuth.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { showAlert } from "../modal/alertModal";

/* ------------------------- 토큰 유틸 ------------------------- */
const ACCESS_KEY = "access_token"; // sessionStorage
const REFRESH_KEY = "refresh_token"; // localStorage

const getAccess = () => sessionStorage.getItem(ACCESS_KEY);
const setAccess = (t) => sessionStorage.setItem(ACCESS_KEY, t || "");
const clearAccess = () => sessionStorage.removeItem(ACCESS_KEY);

const getRefresh = () => localStorage.getItem(REFRESH_KEY);
const setRefresh = (t) => localStorage.setItem(REFRESH_KEY, t || "");
const clearRefresh = () => localStorage.removeItem(REFRESH_KEY);

const productionURL = "https://protocol.x-internal.elva.ai.kr/api/v1";
const localURL = "http://localhost:8081/api/v1";
const productionWS = "wss://protocol.fast-internal.elva.ai.kr";
const localWS = "ws://localhost:3001";
export const BASE_URL = productionURL;
export const BASE_WS = productionWS;

/* ---------------------- Axios 인스턴스 ---------------------- */
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 1_500_000,
});

api.interceptors.request.use((config) => {
  const token = getAccess();
  if (token) config.headers.Authorization = token;
  return config;
});

api.interceptors.response.use(async function (response) {
  if (response.data.notiam) {
    showAlert({
      title: "접근 권한 없음",
      message:
        "워크스페이스 참여자가 아닙니다. 프로젝트 설정 페이지로 이동합니다.",
    });
    return response;
  }
  if (response.data.expired) {
    const res = await axios.get(`${BASE_URL}/issued/token`, {
      headers: { Authorization: getRefresh() || "" },
    });
    if (!res.data.status) {
      clearAccess();
      clearRefresh();
      window.location.href = "/login";
    }
    setAccess(res.data.authorization);
    response.config.headers.Authorization = res.data.authorization;
    return api(response.config);
  }
  return response;
});

/* -------------------- React Context -------------------- */
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(!!getAccess());

  useEffect(() => {
    const tryAuto = async () => {
      // **이미 access_token이 있으면 자동로그인 스킵**
      if (getAccess()) {
        setReady(true);
        return;
      }

      const rt = getRefresh();
      if (!rt) {
        setReady(true);
        return;
      }

      try {
        const { data } = await axios.get(`${BASE_URL}/auto/login`, {
          headers: { Authorization: rt },
        });
        if (data.status) {
          setAccess(data.authorization);
          setRefresh(data.refresh_token);
          setAuthed(true);
        }
        if (data.notiam) {
          window.location.href = "/project_setting";
        }
      } finally {
        setReady(true);
      }
    };

    tryAuto();
  }, []);

  const login = async (id, password) => {
    const { data } = await api.post("/login", { id, password });
    if (!data.status) {
      const msg =
        data.error === "unmatched"
          ? "패스워드가 틀렸습니다."
          : "로그인이 실패하였습니다.";
      throw new Error(msg);
    }

    setAccess(data.authorization);
    setRefresh(data.refresh_token);
    setAuthed(true);
    if (data.notiam) {
      window.location.href = "/project_setting";
    }
  };

  const logout = async () => {
    try {
      await api.get("/logout");
    } catch (_) {
    } finally {
      clearAccess();
      clearRefresh();
      setAuthed(false);
      nav("/login");
    }
  };

  return (
    <AuthCtx.Provider value={{ ready, authed, login, logout, api }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

// 토큰 관리 유틸리티 함수들을 export
export const tokenUtils = {
  getAccess,
  setAccess,
  clearAccess,
  getRefresh,
  setRefresh,
  clearRefresh,
};
